/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2026. All rights Reserved.
 * ===========================================================================
 *
 * The one place that says "your session has expired", and the one that says "this sign-in lacks a
 * permission".
 *
 * There used to be three. main.ts (the boot request) logged out through IDAM. HttpExecutor (any
 * request answered 302) followed the server's redirect, which pointed straight at IDAM sign-in. A
 * third copy sat in HttpExecutor's 401 handler. The boot request runs through HttpExecutor, so the
 * first two fired together, each opening its own SweetAlert. Opening the second closes the first,
 * so both "then" branches ran and whichever navigated last won. On kwstg01 (2026-10-14) that was
 * the signup page.
 * =============================================================================
 */

import {App, createApp} from "vue";
import AppConfig from "@/commons/AppConfig";
import SessionExpiredDialog from "@/components/session/session-expired-dialog.vue";

let shown = false;
let permissionShown = false;
let permissionApp: App | null = null;

/**
 * Show the session-expired dialog, once per page. Its only action clears what is left of the
 * session and lands on the sign-in page with this team filled in (PageHelper.goToSignInAgain).
 * Safe to call before the app is mounted: the dialog is its own small app on its own element.
 */
export function showSessionExpired(): void {
    if (shown) {
        return;
    }
    shown = true;
    // An expired session outranks a missing permission: nothing on the page works, and signing in
    // again fixes both. The boot request can raise the two together (HttpExecutor, then main.ts).
    if (permissionApp != null) {
        permissionApp.unmount();
        permissionApp = null;
    }
    const host = document.createElement("div");
    document.body.appendChild(host);
    createApp(SessionExpiredDialog, {team: AppConfig.Instance.teamSlug}).mount(host);
}

/**
 * Show the "Permission needed" dialog, once per page: IDAM refused this session's token for a
 * permission the page needs (403, errorStatusCode StErrPermissionDenied). Signing in again fixes a
 * token that predates a permission change; it does not fix a user who lacks the permission, and
 * the two look alike from here, so this dialog can be closed and the page stays as it is
 * (SG-TeamSite-Sign-In-Again-On-Missing-Scope-20261015 §2.2). Never shown over "Session expired".
 */
export function showPermissionNeeded(): void {
    if (shown || permissionShown) {
        return;
    }
    permissionShown = true;
    // Loaded when needed: the dialog reads its copy from the i18n module, which needs the profile
    // cookie helpers, and importing it here at load time made a cycle through HttpExecutor.
    import("@/components/session/permission-needed-dialog.vue").then(({default: PermissionNeededDialog}) => {
        if (shown) {
            return;
        }
        const host = document.createElement("div");
        document.body.appendChild(host);
        permissionApp = createApp(PermissionNeededDialog, {team: AppConfig.Instance.teamSlug});
        permissionApp.mount(host);
    });
}
